import '../scss/koc-header.scss';

const HEADER_SELECTOR = '[data-koc-header]';
const BODY_LOCK_CLASS = 'koc-header-body--locked';
const DESKTOP_STANDARD_BREAKPOINT = 1400;

const registry = new Set();
let bodyLockCount = 0;
let listenersBound = false;
let mutationObserver = null;

function lockBodyScroll() {
  bodyLockCount += 1;
  document.body.classList.add(BODY_LOCK_CLASS);
}

function unlockBodyScroll() {
  bodyLockCount = Math.max(0, bodyLockCount - 1);

  if (0 === bodyLockCount) {
    document.body.classList.remove(BODY_LOCK_CLASS);
  }
}

class KocHeader {
  constructor(root) {
    this.root = root;
    this.instanceId = root.getAttribute('data-koc-header-instance') || '';
    this.megaMenuEnabled = 'true' === root.getAttribute('data-koc-header-mega-menu');
    this.menuToggle = root.querySelector('.koc-header__menu-toggle');
    this.searchToggles = Array.from(root.querySelectorAll('[data-koc-header-search-toggle]'));
    this.searchPopover = root.querySelector('.koc-header__search-popover');
    this.searchInput = root.querySelector('.koc-header__search-input');
    this.drawerClose = root.querySelector('.koc-header__drawer-close');
    this.drawer = root.querySelector('.koc-header__main-nav');
    this.backdrop = root.querySelector('.koc-header__backdrop');
    this.submenuItems = Array.from(root.querySelectorAll('.koc-header__menu-item--has-children'));
    this.submenuToggleButtons = Array.from(root.querySelectorAll('.koc-header__submenu-toggle'));
    this.standardDesktopMenuItems = Array.from(root.querySelectorAll('.koc-header__desktop-standard-menu > li.menu-item-has-children'));
    this.isDrawerOpen = false;
    this.isSearchOpen = false;
    this.isInitialized = false;
    this.activeSearchToggle = this.searchToggles[0] || null;
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleViewportChange = this.handleViewportChange.bind(this);
  }

  init() {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;
    this.root.dataset.kocHeaderInitialized = 'true';
    registry.add(this);
    this.bindInstanceEvents();
    this.handleViewportChange();
  }

  bindInstanceEvents() {
    if (this.menuToggle) {
      this.menuToggle.addEventListener('click', () => {
        this.toggleDrawer();
      });
    }

    this.searchToggles.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        this.activeSearchToggle = toggle;
        this.toggleSearch();
      });
    });

    if (this.drawerClose) {
      this.drawerClose.addEventListener('click', () => {
        this.closeDrawer(true);
      });
    }

    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => {
        this.closeDrawer(true);
      });
    }

    this.submenuToggleButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const item = button.closest('.koc-header__menu-item--has-children');

        if (!item) {
          return;
        }

        this.toggleSubmenu(item);

        if (!this.isDrawerOpen) {
          this.openDrawer(false);
        }
      });
    });

    this.standardDesktopMenuItems.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        this.openStandardDesktopSubmenu(item);
      });

      item.addEventListener('mouseleave', () => {
        this.closeStandardDesktopSubmenu(item);
      });

      item.addEventListener('focusin', () => {
        this.openStandardDesktopSubmenu(item);
      });

      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) {
          this.closeStandardDesktopSubmenu(item);
        }
      });
    });

    this.root.addEventListener('click', (event) => {
      const backButton = event.target.closest('[data-koc-header-submenu-back]');
      if (backButton) {
        const item = backButton.closest('.koc-header__menu-item--has-children');

        if (item) {
          event.preventDefault();
          this.closeSubmenu(item, true);
        }

        return;
      }

      if (event.target.closest('.koc-header__submenu-toggle')) {
        return;
      }

      if (event.target.closest('.koc-header__sub-menu-link')) {
        return;
      }

      if (event.target.closest('.koc-header__sub-menu')) {
        return;
      }

      const parentItem = event.target.closest('.koc-header__menu-item--has-children');
      if (parentItem) {
        event.preventDefault();
        this.toggleSubmenu(parentItem);

        if (!this.isDrawerOpen) {
          this.openDrawer(false);
        }

        return;
      }

      const link = event.target.closest('.koc-header__menu-link');
      if (!link) {
        return;
      }

      if (this.isDrawerOpen) {
        this.closeDrawer(false);
      }
    });
  }

  openDrawer(restoreFocus = true) {
    if (this.isDrawerOpen) {
      return;
    }

    this.isDrawerOpen = true;
    this.root.classList.add('koc-header--nav-open');
    lockBodyScroll();

    if (this.menuToggle) {
      this.menuToggle.setAttribute('aria-expanded', 'true');
      this.menuToggle.setAttribute('aria-label', 'Close navigation menu');
    }

    if (this.searchToggles.length) {
      this.searchToggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
      });
    }

    this.closeSearch(false);

    if (restoreFocus) {
      const firstFocusable = this.drawer
        ? this.drawer.querySelector('.koc-header__drawer-close, .koc-header__menu-link, .koc-header__submenu-toggle')
        : null;

      if (firstFocusable instanceof HTMLElement) {
        firstFocusable.focus();
      }
    }
  }

  closeDrawer(restoreFocus = true) {
    if (!this.isDrawerOpen) {
      return;
    }

    this.isDrawerOpen = false;
    this.root.classList.remove('koc-header--nav-open');
    unlockBodyScroll();

    if (this.menuToggle) {
      this.menuToggle.setAttribute('aria-expanded', 'false');
      this.menuToggle.setAttribute('aria-label', 'Open navigation menu');
    }

    this.closeAllSubmenus();

    if (restoreFocus && this.menuToggle instanceof HTMLElement) {
      this.menuToggle.focus();
    }
  }

  toggleDrawer() {
    if (this.isDrawerOpen) {
      this.closeDrawer(true);
      return;
    }

    this.openDrawer(true);
  }

  openSearch(restoreFocus = true) {
    if (this.isSearchOpen) {
      return;
    }

    this.isSearchOpen = true;
    this.root.classList.add('koc-header--search-open');

    if (this.searchPopover) {
      this.searchPopover.hidden = false;
    }

    if (this.searchToggles.length) {
      this.searchToggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'true');
        toggle.setAttribute('aria-label', 'Close search');
      });
    }

    this.closeAllSubmenus();

    if (this.isDrawerOpen) {
      this.closeDrawer(false);
    }

    if (restoreFocus && this.searchInput instanceof HTMLElement) {
      this.searchInput.focus();
      if (typeof this.searchInput.select === 'function') {
        this.searchInput.select();
      }
    }
  }

  closeSearch(restoreFocus = true) {
    if (!this.isSearchOpen) {
      if (restoreFocus && this.activeSearchToggle instanceof HTMLElement) {
        this.activeSearchToggle.focus();
      }
      return;
    }

    this.isSearchOpen = false;
    this.root.classList.remove('koc-header--search-open');

    if (this.searchPopover) {
      this.searchPopover.hidden = true;
    }

    if (this.searchToggles.length) {
      this.searchToggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-label', 'Open search');
      });
    }

    if (restoreFocus && this.activeSearchToggle instanceof HTMLElement) {
      this.activeSearchToggle.focus();
    }
  }

  toggleSearch() {
    if (this.isSearchOpen) {
      this.closeSearch(true);
      return;
    }

    this.openSearch(true);
  }

  openSubmenu(item) {
    const button = item.querySelector('.koc-header__submenu-toggle');

    this.closeAllSubmenus();
    item.classList.add('koc-header__menu-item--expanded');
    this.root.classList.add('koc-header--submenu-open');

    if (button) {
      button.setAttribute('aria-expanded', 'true');
    }
  }

  openStandardDesktopSubmenu(item) {
    if (this.megaMenuEnabled) {
      return;
    }

    if (window.innerWidth < DESKTOP_STANDARD_BREAKPOINT) {
      return;
    }

    item.classList.add('koc-header__desktop-standard-menu-item--hover');
  }

  closeStandardDesktopSubmenu(item) {
    item.classList.remove('koc-header__desktop-standard-menu-item--hover');
  }

  closeAllStandardDesktopSubmenus() {
    this.standardDesktopMenuItems.forEach((item) => {
      this.closeStandardDesktopSubmenu(item);
    });
  }

  closeSubmenu(item, restoreFocus = false) {
    const button = item.querySelector('.koc-header__submenu-toggle');

    item.classList.remove('koc-header__menu-item--expanded');

    if (button) {
      button.setAttribute('aria-expanded', 'false');
    }

    if (!this.submenuItems.some((menuItem) => menuItem.classList.contains('koc-header__menu-item--expanded'))) {
      this.root.classList.remove('koc-header--submenu-open');
    }

    if (restoreFocus && button instanceof HTMLElement) {
      button.focus();
    }
  }

  toggleSubmenu(item) {
    if (item.classList.contains('koc-header__menu-item--expanded')) {
      this.closeSubmenu(item);
      return;
    }

    this.openSubmenu(item);
  }

  closeAllSubmenus() {
    this.submenuItems.forEach((item) => {
      this.closeSubmenu(item, false);
    });

    this.root.classList.remove('koc-header--submenu-open');
  }

  handleViewportChange() {
    if (this.megaMenuEnabled) {
      return;
    }

    if (window.innerWidth < DESKTOP_STANDARD_BREAKPOINT) {
      this.closeAllStandardDesktopSubmenus();
      return;
    }

    this.closeAllStandardDesktopSubmenus();

    if (this.isSearchOpen) {
      this.closeSearch(false);
    }

    if (this.isDrawerOpen) {
      this.closeDrawer(false);
      return;
    }

    this.closeAllSubmenus();
  }

  handlePointerDown(event) {
    if (!(event.target instanceof Node)) {
      return;
    }

    if (this.root.contains(event.target)) {
      return;
    }

    if (this.isSearchOpen) {
      this.closeSearch(false);
    }

    if (this.isDrawerOpen) {
      this.closeDrawer(false);
    }
  }

  handleKeydown(event) {
    if ('Escape' !== event.key) {
      return;
    }

    if (this.isSearchOpen) {
      event.preventDefault();
      this.closeSearch(true);
      return;
    }

    if (this.isDrawerOpen) {
      event.preventDefault();
      this.closeDrawer(true);
    }
  }
}

function initHeader(root) {
  if (!(root instanceof HTMLElement)) {
    return;
  }

  if (root.dataset.kocHeaderInitialized === 'true') {
    return;
  }

  const header = new KocHeader(root);
  header.init();
}

function initAllHeaders() {
  document.querySelectorAll(HEADER_SELECTOR).forEach((root) => {
    initHeader(root);
  });
}

function bindGlobalListeners() {
  if (listenersBound) {
    return;
  }

  listenersBound = true;

  document.addEventListener('keydown', (event) => {
    registry.forEach((instance) => {
      instance.handleKeydown(event);
    });
  });

  document.addEventListener(
    'pointerdown',
    (event) => {
      registry.forEach((instance) => {
        instance.handlePointerDown(event);
      });
    },
    true
  );

  window.addEventListener('resize', () => {
    registry.forEach((instance) => {
      instance.handleViewportChange();
    });
  });

  window.addEventListener('orientationchange', () => {
    registry.forEach((instance) => {
      instance.handleViewportChange();
    });
  });
}

function bindMutationObserver() {
  if (mutationObserver || typeof MutationObserver === 'undefined') {
    return;
  }

  mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) {
          return;
        }

        if (node.matches?.(HEADER_SELECTOR)) {
          initHeader(node);
        }

        node.querySelectorAll?.(HEADER_SELECTOR).forEach((headerRoot) => {
          initHeader(headerRoot);
        });
      });
    });
  });

  mutationObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

function boot() {
  bindGlobalListeners();
  initAllHeaders();
  bindMutationObserver();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
